import { GridView, StackView, Heading, Text, Icon, Box, Dropdown, Badge } from '@planningcenter/tapestry-react';
import React from 'react';
import { useChatContext, useTranslationContext } from 'stream-chat-react';
import { useChannelMute } from '../../hooks/use_channel_mute.js';
import { useChannelUnread } from '../../hooks/use_channel_unread.js';
import { useChat } from '../../hooks/use_chat.js';
import '../../chat_api_context.js';
import '@planningcenter/url';
import { formatDate } from '../../utils/index.js';
import '@planningcenter/jolt-client';
import 'stream-chat';
import colors from '../../vendor/topbar/colors.js';
import { ChannelMetas } from '../channel_metas.js';
import { AvatarGroup } from './avatar_group.js';

const getTimeStamp = (channel, userLanguage) => {
    var _a, _b;
    const lastMessageCreatedAt = (_a = channel.state) === null || _a === undefined ? undefined : _a.last_message_at;
    const conversationCreatedAt = (_b = channel.data) === null || _b === undefined ? undefined : _b.created_at;
    if (!lastMessageCreatedAt && !conversationCreatedAt)
        return null;
    const date = new Date(lastMessageCreatedAt || conversationCreatedAt);
    if (isNaN(date.getTime()))
        return null;
    return formatDate(date, userLanguage);
};
const getLastMessagePreview = (lastMessage) => {
    var _a, _b;
    if (!lastMessage)
        return "Empty conversation";
    const from = (_a = lastMessage.user) === null || _a === undefined ? undefined : _a.name;
    const text = lastMessage.deleted_at ? "Message deleted" : lastMessage.text;
    const attachmentCount = (_b = lastMessage.attachments) === null || _b === undefined ? undefined : _b.length;
    const attachmentString = attachmentCount === 1 ? "1 attachment" : `${attachmentCount} attachments`;
    const messagePreview = text || attachmentString;
    return `${from}: ${messagePreview}`;
};
const UnreadBadge = ({ unread }) => {
    if (unread < 1)
        return null;
    const clampedValue = unread <= 99 ? unread.toString() : "99+";
    return (React.createElement(Badge, { title: clampedValue, size: "xs", color: colors.interaction66, radius: "pill", minWidth: 3 }));
};
const Preview = ({ channel, lastMessage, setActiveChannel, unread }) => {
    const { client } = useChatContext();
    const { userLanguage } = useTranslationContext();
    const { setActionsOpenForChannel, actionsOpenForChannel, navigation, refreshTotalUnreadCount, } = useChat();
    const muted = useChannelMute(channel);
    const { isUnread, isUnreadable, markRead, markUnread, unreadCount } = useChannelUnread(channel, unread, refreshTotalUnreadCount);
    const channelActionsOpen = actionsOpenForChannel === channel.id;
    const handleChannelClick = (e) => {
        const target = e.target;
        if (target.getAttribute("data-type") !== "actions-dropdown") {
            setActiveChannel(channel);
            navigation.switchToChannelView(channel.id);
        }
    };
    const handleChannelKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            handleChannelClick(event);
        }
    };
    const members = Object.values(channel.state.members).filter(({ user }) => user.id !== client.userID);
    const displayTitle = channel.data.name;
    return (React.createElement(GridView, { alignment: "start", "aria-label": `Select Channel: ${displayTitle || ""}`, backgroundColor: channelActionsOpen ? "surfaceTertiary" : "surface", borderBottomColor: "separatorTertiary", borderBottomWidth: 1, columns: "auto 1fr auto", cursor: "pointer", css: {
            "&:hover": {
                ".actions-dropdown": { visibility: "visible" },
                ".channel-meta > *": { borderColor: colors.lightNeutral88 },
                backgroundColor: "surfaceTertiary",
            },
            "@media (hover: none)": {
                ".actions-dropdown": { visibility: "visible" },
            },
        }, minHeight: 10, onClick: handleChannelClick, onKeyDown: handleChannelKeyDown, padding: 1, paddingHorizontal: 2, radius: 0, role: "button", spacing: 1, tabIndex: 0, width: "100%" },
        React.createElement(AvatarGroup, { members: members }),
        React.createElement(StackView, { height: "100%", alignment: "start", paddingRight: 1 },
            React.createElement(Heading, { level: 4, maxWidth: "100%", truncate: true, weight: 500 }, displayTitle),
            React.createElement(Text, { color: "foregroundSecondary", css: {
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: "2",
                    display: "-webkit-box",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }, lineHeight: "1.35", maxWidth: "100%", size: 4, textAlign: "left" }, getLastMessagePreview(lastMessage)),
            React.createElement(StackView, { basis: 0.5, grow: 1 }),
            React.createElement(ChannelMetas, { channel: channel })),
        React.createElement(StackView, { alignment: "end", minWidth: 6, height: "100%" },
            React.createElement(Text, { color: "foregroundSecondary", size: 5 }, getTimeStamp(channel, userLanguage)),
            React.createElement(StackView, { alignment: "end", alignSelf: "stretch", grow: 1 }, muted ? (React.createElement(Icon, { name: "general.bellMuted" })) : (React.createElement(UnreadBadge, { unread: unreadCount }))),
            React.createElement(Box, { onClick: (e) => e.stopPropagation() },
                React.createElement(Dropdown, { alignSelf: "end", className: "actions-dropdown", "data-type": "actions-dropdown", icon: {
                        name: "people.importProgress",
                        size: "lg",
                    }, radius: "circle", iconRight: null, lockScrollWhileOpen: true, onClose: () => setActionsOpenForChannel(null), onOpen: () => setActionsOpenForChannel(channel.id), placement: "top-end", popoverProps: { offset: 4, shouldFlip: true }, right: "1.5px", size: "xs", theme: "dark", title: "Chat actions", variant: "naked", visibility: channelActionsOpen ? "visible" : "hidden" },
                    React.createElement(Dropdown.Item, { disabled: !(isUnread || isUnreadable), onSelect: async () => {
                            isUnread ? await markRead() : await markUnread();
                            await refreshTotalUnreadCount();
                        } }, `Mark ${isUnread ? "read" : "unread"}`),
                    React.createElement(Dropdown.Item, { onSelect: () => (muted ? channel.unmute() : channel.mute()) }, muted ? "Unmute" : "Mute"))))));
};

export { Preview };
//# sourceMappingURL=preview.js.map
